/**
 * liteNavigationUtils.js
 *
 * Utilities for navigating through editable items across the timeline
 * in the Lite View. Handles jumping between time segments when all
 * items at the current time have been exhausted.
 */

import { collectDisplayItems, collectLayerProperties } from './liteDisplayUtils';

const DEFAULT_FPS = 25;

/**
 * Find the nearest time (in seconds) in a given direction that has editable
 * display items.
 *
 * Works by collecting every timeline in/out boundary from elements that carry
 * editable content, then scanning forward or backward for the first boundary
 * where `collectDisplayItems` (or `collectLayerProperties` if showAllProperties is true)
 * returns at least one item.
 *
 * @param {Array}  canvasElements – full element list
 * @param {number} currentTime    – current playhead position in seconds
 * @param {'next'|'prev'} direction
 * @param {number} [fps]
 * @param {boolean} [showAllProperties]
 * @returns {number|null} time in seconds, or null if nothing found
 */
export const findAdjacentMediaTime = (
  canvasElements,
  currentTime,
  direction,
  fps = DEFAULT_FPS,
  showAllProperties = false,
) => {
  if (!canvasElements || canvasElements.length === 0) return null;

  // Collect boundary frames from elements that have editable content
  const boundaryFrames = new Set();

  canvasElements.forEach((el) => {
    const timeline = el.timeline;
    if (!timeline) return;

    const elType = (el.type || '').toLowerCase();
    const hasFields =
      Array.isArray(el.componentData?.fields) && el.componentData.fields.length > 0;
    const isMedia = ['image', 'video', 'audio', 'gif'].includes(elType);
    const isText = elType === 'text';

    // In simple mode, text-only elements are skipped. In showAllProperties they are included.
    if (!hasFields && !isMedia && !(showAllProperties && isText)) return;

    // For agComponents, verify at least one field is editable
    const SIMPLE_KEYS = ['textField', 'imageField', 'videoField', 'audioField'];
    const ALL_KEYS = ['textField', 'imageField', 'videoField', 'audioField', 'colorField'];
    if (hasFields) {
      const keysToCheck = showAllProperties ? ALL_KEYS : SIMPLE_KEYS;
      const hasEditable = el.componentData.fields.some(
        (f) =>
          f &&
          Object.keys(f).some((k) => keysToCheck.includes(k)),
      );
      if (!hasEditable && !showAllProperties) {
        return;
      }

      if (!hasEditable) {
        const hasCaptions = el.componentData?.captions?.phrases && el.componentData.captions.phrases.length > 0;
        if (!(showAllProperties && hasCaptions)) {
          return;
        }
      }
    }

    boundaryFrames.add(timeline.inPoint);
    // Also add the frame after outPoint — that's where a previous element
    // stops being visible and the next segment begins.
    boundaryFrames.add(timeline.outPoint + 1);
  });

  const epsilon = 0.5 / fps; // half-frame tolerance for float comparison
  const sortedTimes = [...boundaryFrames]
    .map((f) => f / fps)
    .sort((a, b) => a - b);

  // Build a fingerprint of the items at a given time for comparison
  const getItemsKey = (t) => {
    if (showAllProperties) {
      const layers = collectLayerProperties(canvasElements, t, fps);
      return layers.map(l => l.elementId).join(',');
    }
    const items = collectDisplayItems(canvasElements, t, fps);
    return items.map(i => `${i.elementId}-${i.fieldIndex ?? 'el'}`).join(',');
  };

  const checkHasItems = (t) => {
    if (showAllProperties) {
      return collectLayerProperties(canvasElements, t, fps).length > 0;
    }
    return collectDisplayItems(canvasElements, t, fps).length > 0;
  };

  // Fingerprint the current time so we can skip boundaries with identical items
  const currentKey = getItemsKey(currentTime);

  if (direction === 'next') {
    for (const t of sortedTimes) {
      if (t > currentTime + epsilon) {
        if (checkHasItems(t) && getItemsKey(t) !== currentKey) return t;
      }
    }
  } else {
    for (let i = sortedTimes.length - 1; i >= 0; i--) {
      const t = sortedTimes[i];
      if (t < currentTime - epsilon) {
        if (checkHasItems(t) && getItemsKey(t) !== currentKey) return t;
      }
    }
  }

  return null;
};
